// §4.10 — THE PUBLIC ORDER TRACKING PAGE, route `/track/$publicToken`.
//
// Same treatment as the quote door: no session, no office chrome, opened from
// a Telegram message on a phone, and its language comes from the ORDER's
// locale rather than the browser's.
//
// THE HONEST TIMELINE IS THE WHOLE DESIGN PROBLEM HERE. §4.10 specifies five
// steps — Confirmed → In production → Ready → Shipped → Delivered — and in P3
// FOUR of them are reached by the system: Confirmed at the mint, In production
// at the first release of a work order made for one of its lines (P3-D PR-D6,
// D-290: module 04 tells module 09 through the peg port, in the release's own
// transaction), and Shipped and Delivered since P3-D PR-D3 (D-286). Only Ready
// still waits: today the office sets it by hand (the status override), and the
// system rule, a completed work order's output reserved to the line, is
// P3-H's. There are two dishonest ways to render that and one honest one:
//
//   (a) draw all five with the unreached ones greyed out and no explanation —
//       which reads as "your order is stuck", and is the single most likely
//       reason a customer rings the factory;
//   (b) draw only the reachable ones — which reads as "that is the whole
//       process", and silently redefines the product;
//   (c) draw the five, mark which have happened WITH THEIR REAL DATES, and say
//       in words that the rest appear as the order moves and that a message
//       comes with each. That is what this does.
//
// The steps that have not happened are not merely a colour: each carries a
// visually-hidden "not yet"/"done" word, because on this page the colour is
// the only signal a screen reader would otherwise have.
//
// CONFIRM RECEIVED (§4.10, PR-D3, D-286 ruling 14) is the page's ONE action,
// and the customer's press is the source of truth for delivery. It is offered
// exactly when the server says so — `can_confirm_received`, computed there as
// `status = 'shipped'` — so the box exists on a shipped order and on nothing
// else, and it confirms first (the accept page's step: a press with weight
// gets a question, never a browser confirm()). The answer is the tracking
// payload itself, which REPLACES the read's cache (hooks/public-queries.ts
// says why it is never refetched): the Delivered step lights from the
// response, the box withdraws because the new payload says so, and a thank-you
// sentence takes its place.
//
// THE SHIPMENTS LIST (P3-E PR-E2, D-292 ruling 12, closing D-286/15's stated
// pending): each shipped shipment with its number, when it left, the carrier
// and the tracking reference when the plant recorded them, and — once it
// arrived — when, and HOW it was confirmed: by the customer's own press here,
// by the office on the carrier's word, or automatically after the tenant's
// confirmation window passed with nobody confirming (`delivered_by: auto`). A
// delivery that confirmed itself is labelled as such, so a customer who never
// pressed anything is not told they did. The list carries NO money and no
// internal reason — the payload has neither, and this list prints none of the
// order's prices even when the tenant opted into showing them on the lines.
//
// PRICES ARE OPT-IN AND OMITTED BY DEFAULT (§4.10 / §2.22
// `tracking_show_prices`). The keys are absent from the payload when the
// tenant has not opted in — the §7 omission rule applied to a tenant setting —
// so this page CANNOT render a price the tenant chose to hide, and the columns
// simply do not exist rather than showing blanks.

import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../components/button";
import { CardSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { formatMinor } from "../../office/inventory/lib/cost";
import { formatBusinessDate, formatTimestamp } from "../../../lib/datetime";
import { PublicFailure, PublicShell } from "../public-shell";
import { publicRenderLocale, type PublicOrderView, type PublicSeller } from "../lib/public-view";
import { usePublicOrder, usePublicOrderConfirmReceived } from "../hooks/public-queries";

/** §4.10's five steps, in order, with the dictionary key each renders under.
 * This list is the CUSTOMER's vocabulary for our `sales_orders.status` values;
 * `closed` and `cancelled` are deliberately not steps — a closed order reads
 * as delivered to the person who received it, and a cancelled one is not a
 * journey with a position on it. */
const STEPS = [
  { status: "confirmed", key: "public.track.stepConfirmed" },
  { status: "in_production", key: "public.track.stepInProduction" },
  { status: "ready", key: "public.track.stepReady" },
  { status: "shipped", key: "public.track.stepShipped" },
  { status: "delivered", key: "public.track.stepDelivered" },
] as const;

function ManagerContact({ seller }: { seller: PublicSeller }) {
  const { t } = useTranslation();
  if (seller.manager_contact_url === null) return null;
  return (
    <p className="public-contact">
      <a
        className="public-link"
        href={seller.manager_contact_url}
        // `noreferrer` for the same reason as the quote page: this page's URL
        // contains the tracking token, and a Referer header would hand it to
        // whatever the link opens in.
        rel="noopener noreferrer"
        target="_blank"
        data-testid="public-manager-contact"
      >
        {seller.manager_name ?? t("public.track.contactManager")}
      </a>
    </p>
  );
}

/** One shipped shipment on the tracking payload (D-292 ruling 12). */
type PublicOrderShipment = components["schemas"]["PublicOrderShipment"];

/** How a delivery was confirmed, as the customer reads it — `null` when the
 * shipment has not arrived yet or the row names no confirmer. */
export function deliveredByKey(shipment: Pick<PublicOrderShipment, "delivered_by">): string | null {
  switch (shipment.delivered_by) {
    case "customer":
      return "public.track.deliveredBy.customer";
    case "carrier":
      return "public.track.deliveredBy.carrier";
    case "auto":
      return "public.track.deliveredBy.auto";
    default:
      return null;
  }
}

/**
 * The payload's shipments, or none. The optional parameter type is the D-230
 * deploy-skew guard, not a type doubt: during a roll an older API task answers
 * without `shipments`, and a page that threw on it would blank the customer's
 * tracking page for the length of the roll. An absent list renders as no list.
 */
function shipmentsOf(view: {
  readonly shipments?: readonly PublicOrderShipment[] | undefined;
}): readonly PublicOrderShipment[] {
  return view.shipments ?? [];
}

/** The shipped shipments — number, when, carrier, tracking reference, and
 * the delivery with how it was confirmed. No money, ever (header). */
function ShipmentList({ shipments }: { readonly shipments: readonly PublicOrderShipment[] }) {
  const { t, i18n } = useTranslation();
  if (shipments.length === 0) return null;
  return (
    <>
      <h2 className="public-subtitle">{t("public.track.shipmentsHeading")}</h2>
      <ul className="public-lines" data-testid="public-track-shipments">
        {shipments.map((shipment) => {
          const how = deliveredByKey(shipment);
          return (
            <li
              className="public-line"
              key={shipment.shipment_no}
              data-testid="public-track-shipment"
              data-shipment-no={shipment.shipment_no}
              data-delivered-by={shipment.delivered_by ?? ""}
            >
              <p className="public-line-name">
                {t("public.track.shipmentNo", { no: shipment.shipment_no })}
              </p>
              <dl className="public-line-facts">
                <dt>{t("public.track.shipmentShipped")}</dt>
                <dd>{formatTimestamp(shipment.shipped_at, i18n.language)}</dd>
                {shipment.carrier !== null ? (
                  <>
                    <dt>{t("public.track.shipmentCarrier")}</dt>
                    <dd data-testid="public-track-shipment-carrier">{shipment.carrier}</dd>
                  </>
                ) : null}
                {shipment.tracking_ref !== null ? (
                  <>
                    <dt>{t("public.track.shipmentTrackingRef")}</dt>
                    <dd data-testid="public-track-shipment-ref">{shipment.tracking_ref}</dd>
                  </>
                ) : null}
                <dt>{t("public.track.shipmentDelivery")}</dt>
                <dd data-testid="public-track-shipment-delivery">
                  {shipment.delivered_at === null
                    ? t("public.track.shipmentOnTheWay")
                    : t("public.track.shipmentDeliveredAt", {
                        when: formatTimestamp(shipment.delivered_at, i18n.language),
                      })}
                  {shipment.delivered_at !== null && how !== null ? (
                    <>
                      {" · "}
                      <span data-testid="public-track-shipment-how">{t(how)}</span>
                    </>
                  ) : null}
                </dd>
              </dl>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function PublicOrderTrackScreen() {
  const { publicToken } = useParams({ strict: false });
  const { t, i18n } = useTranslation();
  const query = usePublicOrder(publicToken ?? "");
  const confirm = usePublicOrderConfirmReceived(publicToken ?? "");
  // The one confirmation step before the press (the accept page's shape).
  const [confirming, setConfirming] = useState(false);
  const view: PublicOrderView | undefined = query.data;

  const locale =
    view !== undefined && "locale" in view ? publicRenderLocale(view.locale) : undefined;

  if (publicToken === undefined || publicToken.length === 0 || view?.state === "not_found") {
    return (
      <PublicShell data-testid="public-track-screen">
        <section className="public-card public-card--quiet" data-testid="public-track-not-found">
          <h1 className="public-title">{t("public.track.notFoundTitle")}</h1>
          <p className="public-body">{t("public.track.notFoundBody")}</p>
        </section>
      </PublicShell>
    );
  }

  if (view === undefined) {
    return (
      <PublicShell data-testid="public-track-screen">
        <CardSkeleton lines={6} label={t("public.shell.loading")} />
      </PublicShell>
    );
  }

  if (view.state === "error") {
    return (
      <PublicShell data-testid="public-track-screen">
        <PublicFailure
          title={t("public.track.errorTitle")}
          body={t("public.track.errorBody")}
          requestId={view.requestId}
          onRetry={() => void query.refetch()}
          data-testid="public-track-error"
        />
      </PublicShell>
    );
  }

  // Which steps have actually happened, from the timeline the server built out
  // of its own timestamps. A step is "done" only when a row exists for it —
  // never inferred from the current status, because a manual status override
  // (§3.6) can move an order forward without minting the stamps in between,
  // and inventing the dates would be inventing history.
  const reached = new Map(view.timeline.map((entry) => [entry.status, entry.at]));
  const showPrices = view.lines.some((line) => line.unit_price_minor !== undefined);
  const money = (minor: number) => formatMinor(minor, view.currency, i18n.language);

  return (
    <PublicShell
      locale={locale}
      sellerName={view.seller.display_name}
      data-testid="public-track-screen"
    >
      <section className="public-card" data-testid="public-track-ok">
        <h1 className="public-title" data-testid="public-track-no">
          {t("public.track.heading", { orderNo: view.order_no })}
        </h1>

        <h2 className="public-subtitle">{t("public.track.statusHeading")}</h2>
        <ol className="public-timeline" data-testid="public-track-timeline">
          {STEPS.map((step) => {
            const at = reached.get(step.status);
            const done = at !== undefined;
            return (
              <li
                className="public-step"
                key={step.status}
                data-step={step.status}
                data-done={done ? "true" : "false"}
                data-testid="public-track-step"
              >
                <span className="public-step-mark" aria-hidden="true" />
                <span className="public-step-name">{t(step.key)}</span>
                {/* The state as a WORD, not only as a colour — this page has
                    no other affordance a screen reader could read it from. */}
                <span className="visually-hidden">
                  {done ? t("public.track.stepDone") : t("public.track.stepPending")}
                </span>
                {done ? (
                  <span className="public-step-at">{formatTimestamp(at, i18n.language)}</span>
                ) : null}
              </li>
            );
          })}
        </ol>

        {/* The page's one action (header). The thank-you stands FIRST in the
            chain: once the press landed on this mount, the replaced payload
            already reads `can_confirm_received: false`, and the sentence is
            what the customer sees where the button was. */}
        {confirm.isSuccess ? (
          <p className="public-body" role="status" data-testid="public-track-confirm-done">
            {t("public.track.confirmReceivedDone")}
          </p>
        ) : view.can_confirm_received ? (
          <div className="public-confirm" data-testid="public-track-confirm-box">
            <p className="public-confirm-title">{t("public.track.confirmReceivedTitle")}</p>
            {confirm.isError ? (
              <p className="public-alert" role="alert" data-testid="public-track-confirm-failed">
                {t("public.track.confirmReceivedFailed")}
              </p>
            ) : null}
            {confirming ? (
              <>
                <p className="public-body">{t("public.track.confirmReceivedAsk")}</p>
                <div className="public-actions">
                  <Button
                    variant="primary"
                    busy={confirm.isPending}
                    busyLabel={t("public.track.confirmReceivedYes")}
                    data-testid="public-track-confirm-yes"
                    onClick={() => {
                      confirm.mutate();
                    }}
                  >
                    {t("public.track.confirmReceivedYes")}
                  </Button>
                  <Button
                    data-testid="public-track-confirm-no"
                    disabled={confirm.isPending}
                    onClick={() => {
                      setConfirming(false);
                      confirm.reset();
                    }}
                  >
                    {t("public.track.confirmReceivedNo")}
                  </Button>
                </div>
              </>
            ) : (
              <div className="public-actions">
                <Button
                  variant="primary"
                  className="public-action"
                  data-testid="public-track-confirm"
                  onClick={() => {
                    setConfirming(true);
                  }}
                >
                  {t("public.track.confirmReceived")}
                </Button>
              </div>
            )}
          </div>
        ) : null}

        {/* §3.5d's stated pending: module 03 owns the promise date and is not
            built, so the page names what is missing instead of rendering an
            empty "Ready by" that reads as "we do not know and will not say". */}
        {view.promise_date === null ? (
          <p className="public-body" data-testid="public-track-promise-pending">
            {t("public.track.promisePending")}
          </p>
        ) : (
          <p className="public-validity" data-testid="public-track-promise">
            <StatusChip variant="neutral">
              {t("public.track.promiseDate", {
                date: formatBusinessDate(view.promise_date, i18n.language),
              })}
            </StatusChip>
          </p>
        )}

        <p className="public-body" data-testid="public-track-stages-pending">
          {t("public.track.stagesPending")}
        </p>

        <ShipmentList shipments={shipmentsOf(view)} />

        <ul className="public-lines" data-testid="public-track-lines">
          {view.lines.map((line) => (
            <li className="public-line" key={line.line_no} data-testid="public-track-line">
              <p className="public-line-name">{line.description}</p>
              <dl className="public-line-facts">
                <dt>{t("public.track.colQty")}</dt>
                <dd className="num">
                  {line.qty_ordered} {line.uom_code}
                </dd>
                <dt>{t("public.track.colShipped")}</dt>
                <dd className="num" data-testid="public-track-line-shipped">
                  {line.qty_shipped} {line.uom_code}
                </dd>
                {/* §2.22's toggle: when the tenant has not opted in these keys
                    are ABSENT from the payload, so there is no cell at all —
                    never a blank one that invites "why is this empty?". */}
                {line.unit_price_minor !== undefined ? (
                  <>
                    <dt>{t("public.track.colUnitPrice")}</dt>
                    <dd className="num">{money(line.unit_price_minor)}</dd>
                  </>
                ) : null}
                {line.line_total_minor !== undefined ? (
                  <>
                    <dt>{t("public.track.colTotal")}</dt>
                    <dd className="num">{money(line.line_total_minor)}</dd>
                  </>
                ) : null}
              </dl>
            </li>
          ))}
        </ul>

        {showPrices && view.total_minor !== undefined ? (
          <dl className="public-totals" data-testid="public-track-totals">
            <dt className="public-total-label">{t("public.track.total")}</dt>
            <dd className="num public-total-value">{money(view.total_minor)}</dd>
          </dl>
        ) : null}

        <ManagerContact seller={view.seller} />
      </section>
    </PublicShell>
  );
}
